import { flash } from "@xola/ui-kit";
import React from "react";
import { useHistory } from "react-router-dom";
import { CouponCode, GiftOffer, PurchaseItemObject } from "types";
import { api } from "../../api/api";
import { useSeller } from "../../hooks/useAccount";
import { useVisibility } from "../../hooks/useVisibility";
import { CreatePurchaseState, useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { Response } from "../../types/Response";
import { log } from "../../utils/log";
import { OverlayLoader } from "./OverlayLoader";
import { QrCodeScanner } from "./QrCodeScanner";

export enum ScanObject {
    GIFT = "gift",
}

export const GiftQrCodeScanner = () => {
    const seller = useSeller();
    const history = useHistory();
    const [isLoading, { show: showLoader, hide: hideLoader }] = useVisibility(false);

    const fetchCouponCode = async (code: string) => {
        const { data } = await api.get<Response<CouponCode[], any>>(`couponCodes`, {
            params: { seller: seller.id, code, couponLimit: -1 },
        });
        return data?.data ?? [];
    };

    const fetchBaseGiftProduct = async () => {
        const { data } = await api.get<Response<GiftOffer, any>>("giftOffers", {
            params: { seller: seller.id, baseGiftProduct: true, limit: 1 },
        });

        return data.data?.[0];
    };

    const handleGiftScan = async (couponCode: string, data: string[]) => {
        if (data.length !== 4) {
            flash.danger("Unrecognizable gift card code");
            hideLoader();
            return;
        }

        const [couponCodes, baseGiftProduct] = await Promise.all([fetchCouponCode(couponCode), fetchBaseGiftProduct()]);
        const isDuplicateCode = couponCodes.length > 0;

        if (isDuplicateCode) {
            flash.danger("The scanned gift card has a duplicate code, please scan another gift card");
            hideLoader();
            return;
        }

        if (!baseGiftProduct) {
            flash.danger("Error in creating gift");
            hideLoader();
            return;
        }

        const giftCode = data[1];
        const giftValue = data[3];

        const { purchase } = useCreatePurchaseStore.getState();
        if (purchaseHasDuplicateCode(purchase, giftCode)) {
            flash.danger(
                "The scanned gift card has a duplicate code with one of the gift / gift card in progress, please scan another gift card",
            );
            hideLoader();
            return;
        }

        hideLoader();
        history.push(`/cart?product=${baseGiftProduct?.id}&giftCode=${giftCode}&giftValue=${giftValue}`);
    };

    const handleQRCodeScan = (scannedData: string) => {
        showLoader();
        const data = scannedData.toLowerCase().split("-");
        const [objectType, objectId] = data;
        if (objectType === ScanObject.GIFT) {
            handleGiftScan(objectId, data);
        } else {
            flash.danger("Scanned code is invalid");
            log("Scanned code is invalid", { objectType, scannedData });
        }
    };

    return (
        <>
            <QrCodeScanner onComplete={handleQRCodeScan} />
            <OverlayLoader isOpen={isLoading} color="primary" />
        </>
    );
};

const purchaseHasDuplicateCode = (purchase: CreatePurchaseState, code: string) => {
    const items = purchase.items.filter((item) => item.object === PurchaseItemObject.GIFT);
    return items.some((item) => item.code === code);
};
